
import { useState } from 'react';
import { businessUtils } from '@/context/utils/businessUtils'; // Replaced useDataUtils
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { BusinessStatus, Business as BusinessZod, Member as MemberZod } from '@/schemas/business'; // Using Zod types
import { 
  updateBusiness, 
  updatePortfolio, 
  addUserToPortfolio,
  PORTFOLIOS_COLLECTION, 
  BUSINESS_COLLECTION
} from '@/utils/firestoreService';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore'; // Added serverTimestamp
import { db } from '@/utils/firebase';
import { businessesCollection } from '@/utils/firestore/refs';
import { MemberPermission, MemberRole } from '@/types/schema';
import { addBusinessToUser } from '@/utils/firestore/users';

// Define BusinessDetails interface
export interface BusinessDetails {
  name: string;
  shortName?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  industry: string;
  founded?: string;
  coordinates?: { lat: number; lng: number };
}

export const useBusinessSubmit = (
  portfolioId: string,
  portfolioName: string = 'Portfolio',
  onOpenChange: (open: boolean) => void
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const { addBusiness } = useDataUtils(); // Removed
  const { toast } = useToast();
  const navigate = useNavigate();
  const auth = getAuth();

  const handleSubmit = () => {
    try {
      setIsSubmitting(true);
      
      // For now just stub out the actual functionality
      // We'll use the businessDetails from the context when calling this function
      const submitBusiness = async (businessDetails: BusinessDetails) => {
        try {
          // Create initial location data with business coordinates if available
          const initialLocationData = {
            targetAllUS: true,
            targetedStates: [],
            locationsByRadius: [],
            radiusMiles: 50,
            blockedZipCodes: [],
            businessCoordinates: businessDetails.coordinates ? {
              latitude: businessDetails.coordinates.lat,
              longitude: businessDetails.coordinates.lng
            } : undefined
          };
          
          const currentDate = new Date().toISOString();
          const generatedBusinessId = uuidv4(); // Generate business ID
          
          const currentUser = auth.currentUser;
          if (!currentUser) {
            throw new Error("No authenticated user found");
          }
          
          const now = new Date(); // For timestamps

          // Prepare core business data for businessUtils.addBusiness
          // This excludes id, portfolioId, createdAt, updatedAt as per businessUtils.addBusiness signature
          const coreBusinessData = {
            name: businessDetails.name,
            // location: `${businessDetails.city}, ${businessDetails.state}`, // Not a direct field in BusinessZod, part of address logic
            address: `${businessDetails.address}, ${businessDetails.city}, ${businessDetails.state} ${businessDetails.zip}`,
            phone: businessDetails.phone,
            website: businessDetails.website,
            email: businessDetails.email,
            status: 'onboarding' as BusinessStatus,
            industry: businessDetails.industry,
            founded: businessDetails.founded,
            // displayName and shortName can be added if needed, map from businessDetails.name or shortName
            displayName: businessDetails.name, 
            shortName: businessDetails.shortName || businessDetails.name,
            // coordinates are part of locationData
            coordinates: businessDetails.coordinates ? { latitude: businessDetails.coordinates.lat, longitude: businessDetails.coordinates.lng } : undefined,
            locationData: initialLocationData, // This includes coordinates
            // Ensure members structure matches Omit<MemberZod, 'invitedAt'|'joinedAt'> & { invitedAt: string | null, joinedAt: string | null }
            // businessUtils.addBusiness will handle serverTimestamps for createdAt/updatedAt for the business
            // and expects ISO strings or null for member invitedAt/joinedAt if provided, or handles them.
            // For simplicity, let businessUtils handle member timestamps if it converts them.
            // The newBusiness object below was for the old addBusiness.
            // The members array for coreBusinessData:
            members: [
              {
                userId: currentUser.uid,
                role: "Primary Owner" as MemberRole, // Assuming MemberRole is compatible or z.string()
                permissions: [
                  { id: 'fullAccess', name: 'Full Access', checked: true, description: 'Complete access to all business features' },
                ],
                status: 'active' as 'active' | 'invited', // Assuming status is compatible or z.string()
                email: currentUser.email || undefined, // Match optional schema fields
                firstName: currentUser.displayName?.split(' ')[0] || undefined,
                lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || undefined,
                // invitedAt and joinedAt will be handled by businessUtils.addBusiness if it converts Date to serverTimestamp
                // or expects ISO strings. For now, pass Date objects.
                // The type in businessUtils.addBusiness is: { invitedAt: string | null, joinedAt: string | null }
                // So, convert Date to ISO string.
                invitedAt: now.toISOString(),
                joinedAt: now.toISOString()
              }
            ]
          };
          
          console.log("Submitting business with core data:", coreBusinessData);

          // 1. Create the business document in Firestore using businessUtils.addBusiness
          // This function handles setDoc with server timestamps for the business.
          await businessUtils.addBusiness(portfolioId, generatedBusinessId, coreBusinessData as any); 
          // Using `as any` for coreBusinessData due to complex Omit/Pick types, Zod validation is inside addBusiness.

          // 2. Update the portfolio document to include this business reference
            const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId);
            await updateDoc(portfolioRef, {
              businessRefs: arrayUnion(generatedBusinessId), // Assuming portfolio stores businessRefs
              updatedAt: serverTimestamp() // Use serverTimestamp
            });
            
            // 3. Update the user document using the new utility function
            await addBusinessToUser(currentUser.uid, generatedBusinessId);
            
            console.log("Business relationships updated in Firestore");
          
          // Show success toast
          toast({
            title: "Business added",
            description: `${businessDetails.name} has been added to ${portfolioName}`,
          });
          
          // Close the modal
          onOpenChange(false);
          
          // Navigate to the onboarding page for the new business
          setTimeout(() => {
            navigate(`/business/${generatedBusinessId}/onboarding`);
          }, 100);
        } catch (error) {
          console.error('Error creating business:', error);
          toast({
            title: "Error",
            description: "Failed to create business. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsSubmitting(false);
        }
      };
      
      return submitBusiness;
    } catch (error) {
      console.error('Error setting up business submit:', error);
      setIsSubmitting(false);
      return async () => {}; // Return empty function in case of error
    }
  };

  return {
    handleSubmit,
    isSubmitting
  };
};
